import { datalayerPush } from '@/features/shared/HOCs/withGTM';
import Image from '@/features/shared/Image';
import { MuiButtonBase } from '@/features/shared/material-ui';
import SwiperCarousel from '@/features/shared/SwiperCarousel';
import { ColorNames, IS_PREVIEW } from '@/utils/constants';
import { mapLinkToProps } from '@/utils/helpers';
import { isUserInteraction } from '@/utils/swiper.helpers';
import clsx from 'clsx';
import React, { memo, useMemo, useRef, useState } from 'react';
import TagManager from 'react-gtm-module';
import Swiper from 'swiper';
import { SwiperSlide } from 'swiper/react';
import { LogoCardCarouselProps } from './LogoCardCarousel.interface';
import useStyles from './LogoCardCarousel.style';
import { CMSPlaceholder } from '@/features/shared';

const LogoCardCarousel = ({
  content,
  cardColor = ColorNames.charcoal,
  theme,
  contentButtons,
  hideCTA,
  hideNavigation,
  autoscroll,
  autoscrollSpeed = 1,
  autoscrollDirection,
}: Br.PropsWithContentButtons<LogoCardCarouselProps>) => {
  const classes = useStyles({});
  const rootClassName = clsx(classes.root, `${theme}-theme`);
  const { contentList = [], cta } = content;
  const filteredContentList = useMemo(
    () => contentList.filter((card) => card.cardLogo),
    [contentList],
  );
  const [slider, setSlider] = useState<any>(null);
  const [slideIndex, setSlideIndex] = useState<number>();
  const containerRef = useRef<HTMLDivElement>(null);
  const isFirstLoad = useRef(true);
  const [triggerCarouselNavClick, setTriggerCarouselNavClick] = React.useState(false);

  React.useEffect(() => {
    if (!triggerCarouselNavClick) return;
    if (isFirstLoad.current) {
      isFirstLoad.current = false;
      return;
    }

    TagManager.dataLayer({
      dataLayer: {
        event: 'trackEvent-listCardCarouselNavClick',
        event_name: 'list_card_carousel_nav_click',
        title: filteredContentList?.[slideIndex || 0]?.cardHeading,
        index_num: String(slideIndex),
      },
    });
    datalayerPush({
      compRef: containerRef,
      args: {
        slideIndex,
        variantName: (containerRef?.current?.closest('[data-datalayer-variant]') as HTMLDivElement)
          ?.dataset?.datalayerVariant,
      },
      event: 'gtm:custom:scroll',
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [triggerCarouselNavClick]);

  const configuration = useMemo(() => {
    const autoscrollProps = autoscroll
      ? {
          autoplay: {
            reverseDirection: autoscrollDirection && autoscrollDirection === 'right',
            delay: 0,
          },
          speed: autoscrollSpeed * 1000,
        }
      : false;

    return {
      centerInsufficientSlides: true,
      centeredSlides: true,
      spaceBetween: 20,
      slidesPerView: 'auto',
      pagination: { clickable: true, el: `.${classes.sliderNavigation}` },
      loop: true,
      loopedSlides: filteredContentList.length,
      ...autoscrollProps,
      onSwiper: (swiper: Swiper) => setSlider(swiper),
      onSlideChangeTransitionEnd: (swiperInstance: Swiper) => {
        const { realIndex } = swiperInstance;
        if (isUserInteraction(swiperInstance)) {
          setTriggerCarouselNavClick(false);
          setSlideIndex(realIndex);

          Promise.resolve().then(() => {
            setTriggerCarouselNavClick(() => true);
          });
        }
      },
    };
  }, [
    classes.sliderNavigation,
    filteredContentList.length,
    autoscroll,
    autoscrollSpeed,
    autoscrollDirection,
  ]);

  if (filteredContentList.length === 0) {
    if (IS_PREVIEW) {
      return <CMSPlaceholder componentName="CardCarousel" errorMessage="No cards to display" />;
    }

    return null;
  }

  return (
    <div className={rootClassName} ref={containerRef}>
      {contentButtons?.content}
      <SwiperCarousel
        theme={theme}
        mdTheme={theme}
        cta={!hideCTA ? cta : null}
        navigation={!hideNavigation}
        slider={slider}
        classes={classes}
        {...configuration}
      >
        {filteredContentList.map((card, idx: number) => {
          const linkProps = mapLinkToProps(card.buttonLink);
          const cardClassName = clsx(classes.card, `bg-color-${cardColor}`);
          return (
            <SwiperSlide
              // eslint-disable-next-line react/no-array-index-key
              key={`card_${idx}`}
              className={classes.slide}
              data-datalayer-card-index={idx}
              data-datalayer-card-title={card.cardHeading}
            >
              <div className={cardClassName}>
                <MuiButtonBase {...linkProps} aria-label={card?.cardHeading}>
                  <Image
                    image={(card as CardType).cardLogo ?? {}}
                    className={classes.image}
                    size="contain"
                    roundedCorners={false}
                  />
                </MuiButtonBase>
              </div>
            </SwiperSlide>
          );
        })}
      </SwiperCarousel>
    </div>
  );
};

export default memo(LogoCardCarousel);
